// @ts-strict-ignore
import {
  AXIS_HIGH_UNBOUNDED_NUMBER,
  AXIS_LOW_UNBOUNDED_NUMBER,
  DEFAULT_INIT_AXIS,
  getColOrRow,
  HighRangeOption,
  LowRangeOption,
  MatrixAxisOperation,
  updateMatrixSize,
} from 'flow/service/actions';
import type { MatrixModel } from 'flow/store/engine/types';

import type {
  AxisOptions,
  UpdateAxisSettingsProps,
} from './types';
import {
  AxisOrderOption,
  AxisTypeOption,
} from './types';
import type { Axis } from '../AxisHeaderDropdown';


// --- CONVERT MATRIX MODEL AXIS -> AXIS OPTIONS: --------------

export const getAxisType = (
  dataType: MatrixModel['axis']['x' | 'y']['axisHeaderDataType'],
) => {
  switch (dataType) {
    case 'number':
      return AxisTypeOption.range;
    case 'string':
      return AxisTypeOption.exact;
    default:
      return null;
  }
};

export const getAxisOrder = (
  axisOrder: MatrixModel['axis']['x' | 'y']['order'],
  axisType: AxisTypeOption,
) => {
  if (axisType === AxisTypeOption.exact) return null;

  return axisOrder === 'asc'
    ? AxisOrderOption.asc
    : AxisOrderOption.desc;
};

export const getHighRange = (
  axisHeader: MatrixModel['axis']['x' | 'y']['axisHeader'],
  axisType: AxisTypeOption,
) => {
  if (axisType === AxisTypeOption.exact) return null;

  if (axisHeader.includes(AXIS_HIGH_UNBOUNDED_NUMBER)) {
    return HighRangeOption.unbounded;
  }
  return HighRangeOption.bounded;
};

export const getLowRange = (
  axisHeader: MatrixModel['axis']['x']['axisHeader'],
  axisType: AxisTypeOption,
) => {
  if (axisType === AxisTypeOption.exact) return null;

  if (axisHeader.includes(AXIS_LOW_UNBOUNDED_NUMBER)) {
    return LowRangeOption.unbounded;
  }
  return LowRangeOption.bounded;
};

export const getAxisOptions = (
  axisSettings: MatrixModel['axis']['x' | 'y'],
): AxisOptions => {
  const axisType = getAxisType(axisSettings.axisHeaderDataType);
  return {
    axisType,
    axisOrder: getAxisOrder(axisSettings.order, axisType),
    highRange: getHighRange(axisSettings.axisHeader, axisType),
    lowRange: getLowRange(axisSettings.axisHeader, axisType),
  };
};

// --- HANDLE UPDATING MATRIX MODEL AXIS WITH AXIS OPTIONS: ---

export const handleHighRangeBoundedToUnbounded = (
  axisHeader: MatrixModel['axis']['x' | 'y']['axisHeader'],
  order: MatrixModel['axis']['x' | 'y']['order'],
) => (order === 'asc'
  ? [...axisHeader, AXIS_HIGH_UNBOUNDED_NUMBER]
  : [AXIS_HIGH_UNBOUNDED_NUMBER, ...axisHeader]);

export const handleHighRangeUnboundedToBounded = (
  axisHeader: MatrixModel['axis']['x' | 'y']['axisHeader'],
  order: MatrixModel['axis']['x' | 'y']['order'],
) => {
  if (axisHeader.length === 2) {
    // a range axis array should always have atleast a length of 2,
    // so if it starts with 2 values and we remove the unbounded value,
    // it should end with a length of 2.
    // Null adds to the length but indicates that the user needs to input a value
    if (order === 'asc') return [axisHeader[0], null];
    return [axisHeader[1], null];
  }

  return (order === 'asc'
    ? axisHeader.slice(0, -1)
    : axisHeader.slice(1));
};

export const handleLowRangeBoundedToUnbounded = (
  axisHeader: MatrixModel['axis']['x' | 'y']['axisHeader'],
  order: MatrixModel['axis']['x' | 'y']['order'],
) => (order === 'asc'
  ? [AXIS_LOW_UNBOUNDED_NUMBER, ...axisHeader]
  : [...axisHeader, AXIS_LOW_UNBOUNDED_NUMBER]);

export const handleLowRangeUnboundedToBounded = (
  axisHeader: MatrixModel['axis']['x' | 'y']['axisHeader'],
  order: MatrixModel['axis']['x' | 'y']['order'],
) => {
  if (axisHeader.length === 2) {
    // a range axis array should always have atleast a length of 2,
    // so if it starts with 2 values and we remove the unbounded value,
    // it should end with a length of 2.
    // Null adds to the length but indicates that the user needs to input a value
    if (order === 'asc') return [axisHeader[1], null];
    return [axisHeader[0], null];
  }

  return order === 'asc'
    ? axisHeader.slice(1)
    : axisHeader.slice(0, -1);
};


export const handleRangeUpdates = ({
  axisHeader, initialOptions, selectedOptions,
}: UpdateAxisSettingsProps): MatrixModel['axis']['x' | 'y'] => {
  const updatedRangeSettings = {
    axisHeader,
    axisHeaderDataType: 'number',
    order: initialOptions.axisOrder,
  };

  // if updating sort order:
  if (initialOptions.axisOrder !== selectedOptions.axisOrder) {
    updatedRangeSettings.axisHeader = [...axisHeader].reverse();
    updatedRangeSettings.order = selectedOptions.axisOrder;
  }

  // if updating high range:
  if (initialOptions.highRange !== selectedOptions.highRange) {
    if (selectedOptions.highRange === HighRangeOption.bounded) {
      updatedRangeSettings.axisHeader = handleHighRangeUnboundedToBounded(
        updatedRangeSettings.axisHeader, updatedRangeSettings.order,
      );
    } else {
      updatedRangeSettings.axisHeader = handleHighRangeBoundedToUnbounded(
        updatedRangeSettings.axisHeader, updatedRangeSettings.order,
      );
    }
  }

  // if updating low range:
  if (initialOptions.lowRange !== selectedOptions.lowRange) {
    if (selectedOptions.lowRange === LowRangeOption.bounded) {
      updatedRangeSettings.axisHeader = handleLowRangeUnboundedToBounded(
        updatedRangeSettings.axisHeader, updatedRangeSettings.order,
      );
    } else {
      updatedRangeSettings.axisHeader = handleLowRangeBoundedToUnbounded(
        updatedRangeSettings.axisHeader, updatedRangeSettings.order,
      );
    }
  }

  return updatedRangeSettings;
};

export const updateAxisSettings = ({
  axisHeader, initialOptions, selectedOptions,
}: UpdateAxisSettingsProps,
): MatrixModel['axis']['x' | 'y'] => {
  if (initialOptions.axisType === AxisTypeOption.range) {
    // if changing to exact:
    if (selectedOptions.axisType === AxisTypeOption.exact) {
      return {
        axisHeaderDataType: 'string',
        // when updating from range to exact,
        // in order for the axis headers to match matrix size
        // we need to update length to (axisHeader.length - 1)
        axisHeader: Array(axisHeader.length - 1).fill(null),
        order: null,
      };
    }

    return handleRangeUpdates({
      axisHeader, initialOptions, selectedOptions,
    });
  }

  if (initialOptions.axisType === AxisTypeOption.exact) {
    // if changing to range:
    if (selectedOptions.axisType === AxisTypeOption.range) {
      // when updating from exact to range,
      // in order for the axis headers to match matrix size
      // we need to update length to (axisHeader length + 1):
      let countToUpdateLength = 0; // already accounts for high unbounded
      if (selectedOptions.highRange === HighRangeOption.bounded) {
        countToUpdateLength -= 1;
      }
      if (selectedOptions.lowRange === LowRangeOption.unbounded) {
        countToUpdateLength += 1;
      }
      const initAxisHeader = (axisHeader.length === 1)
        ? [null, AXIS_HIGH_UNBOUNDED_NUMBER]
        : [
          ...Array(axisHeader.length - countToUpdateLength).fill(null),
          AXIS_HIGH_UNBOUNDED_NUMBER,
        ];

      // and then reset all options:
      const initAxisSettings = getAxisOptions({
        ...DEFAULT_INIT_AXIS,
        axisHeader: initAxisHeader,
      });
      return handleRangeUpdates({
        axisHeader: initAxisHeader,
        initialOptions: initAxisSettings,
        selectedOptions,
      });
    }
    // no changes:
    return {
      axisHeader,
      axisHeaderDataType: 'string',
      order: null,
    };
  }

  return null;
};

export const updateMatrixSizeToMatchHeaders = ({
  axis, axisSettings, matrix,
}: {
  axis: Axis;
  axisSettings: MatrixModel['axis']['x' | 'y'];
  matrix: MatrixModel['matrix'];
  row?: number;
}) => {
  const { axisHeader, axisHeaderDataType } = axisSettings;
  const group = getColOrRow(axis);

  const lengthOfCurrent = group === 'column'
    ? matrix[0].length : matrix.length;

  const lengthToMatch = axisHeaderDataType === 'string'
    // when axis data type is number but there is only 1 value
    // matrix length should still equal to 1 to match header
    || axisHeader.length === 1
    ? axisHeader.length : axisHeader.length - 1;

  if (lengthOfCurrent === lengthToMatch) return matrix;

  let operation: MatrixAxisOperation;
  let count: number;
  if (lengthOfCurrent > lengthToMatch) {
    operation = MatrixAxisOperation.DELETE;
    count = lengthOfCurrent - lengthToMatch;
  } else {
    operation = MatrixAxisOperation.ADD;
    count = lengthToMatch - lengthOfCurrent;
  }

  return updateMatrixSize({
    axis, matrix, op: operation, count,
  });
};
